/*  global_dpm_ui.tsx — small table helpers shared by the /global-dpm tabs:
    click-to-sort headers and an expanded-row set. No data logic here. */
import React, { ReactNode, useMemo, useState } from 'react';
import { Card, CardProps, Table } from '@mantine/core';
import { IconChevronDown, IconChevronRight, IconSortAscending, IconSortDescending } from '@tabler/icons-react';

type SortValue = string | number | boolean | undefined | null;
type Getters<T> = Record<string, (r: T) => SortValue>;

/** rows sorted by the clicked header; `th(key, label)` renders the header cell for `getters[key]`. */
export function useSort<T>(rows: T[], getters: Getters<T>, initialKey?: string, initialDir: 'asc' | 'desc' = 'asc') {
    const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' }>({ key: initialKey ?? '', dir: initialDir });
    const sorted = useMemo(() => {
        const g = getters[sort.key];
        if (!g) return rows;
        const dir = sort.dir === 'asc' ? 1 : -1;
        return [...rows].sort((a, b) => {
            const x = g(a); const y = g(b);
            if (x == null && y == null) return 0;
            if (x == null) return 1;
            if (y == null) return -1;
            if (typeof x === 'number' && typeof y === 'number') return (x - y) * dir;
            return String(x).localeCompare(String(y), undefined, { numeric: true }) * dir;
        });
    }, [rows, sort, getters]); // eslint-disable-line react-hooks/exhaustive-deps
    const th = (key: string, label: ReactNode, ta: 'left' | 'right' = 'left') => (
        <Table.Th key={key} ta={ta} style={{ cursor: 'pointer', whiteSpace: 'nowrap', userSelect: 'none' }}
            onClick={() => setSort((s) => ({ key, dir: s.key === key && s.dir === 'asc' ? 'desc' : 'asc' }))}>
            {label}{sort.key === key && (sort.dir === 'asc' ? <IconSortAscending size={12} style={{ marginLeft: 4, verticalAlign: 'middle' }} /> : <IconSortDescending size={12} style={{ marginLeft: 4, verticalAlign: 'middle' }} />)}
        </Table.Th>
    );
    return { sorted, th, sort };
}

export const useExpanded = () => {
    const [open, setOpen] = useState<Set<string>>(new Set());
    const toggle = (id: string) => setOpen((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
    const isOpen = (id: string) => open.has(id);
    return { isOpen, toggle, openAll: (ids: string[]) => setOpen(new Set(ids)), closeAll: () => setOpen(new Set()) };
};

export const Chevron = ({ open }: { open: boolean }) => (open ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />);

/** A detail row spanning the table: rendered only while expanded (no Collapse — hidden Mantine panels suspend effects). */
export const DetailRow = ({ open, colSpan, children }: { open: boolean; colSpan: number; children: ReactNode }) =>
    open ? <Table.Tr><Table.Td colSpan={colSpan} p="md" pl="xl" bg="white" style={{ borderBottom: '2px solid var(--mantine-color-gray-3)' }}>{children}</Table.Td></Table.Tr> : null;

/** White card every table on the page sits in (the Composite look). */
export const Panel = ({ children, ...props }: { children: ReactNode } & Omit<CardProps, 'children'>) => (
    <Card withBorder padding="sm" radius="md" bg="white" {...props}>{children}</Card>
);
